<template>
  <!-- mount 后再 Teleport，避免 SSR 水合阶段 body 目标不一致 -->
  <Teleport v-if="mounted" to="body">
    <button
      type="button"
      class="ui-scroll-to-top"
      :class="{ 'ui-scroll-to-top--visible': visible }"
      :aria-label="resolvedAriaLabel"
      :tabindex="visible ? 0 : -1"
      @click="scrollToTop"
    >
      <svg class="ui-scroll-to-top__progress" viewBox="0 0 40 40" aria-hidden="true">
        <circle class="ui-scroll-to-top__track" cx="20" cy="20" :r="RING_RADIUS" />
        <circle
          class="ui-scroll-to-top__ring"
          cx="20"
          cy="20"
          :r="RING_RADIUS"
          :stroke-dasharray="RING_CIRCUMFERENCE"
          :stroke-dashoffset="ringOffset"
        />
      </svg>
      <svg
        class="ui-scroll-to-top__icon"
        viewBox="0 0 1024 1024"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          d="M574.4 346.88V832c0 35.328-28.8 64-64.32 64a64.128 64.128 0 0 1-64.32-64V346.88L234.176 557.44c-25.6 21.76-63.552 20.288-87.36-3.328a63.744 63.744 0 0 1-3.328-86.848l315.136-313.6a64.256 64.256 0 0 1 100.352-3.2l318.336 316.8a63.808 63.808 0 0 1-0.32 90.56 64.64 64.64 0 0 1-91.008-0.32L574.4 346.88z"
        />
      </svg>
    </button>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import type { SiteFooterLocale } from '../consts/gcsaFooterContent';
import { SCROLL_TO_TOP_LABELS } from '../consts/gcsaA11yLabels';

const props = withDefaults(
  defineProps<{
    locale?: SiteFooterLocale;
    ariaLabel?: string;
    threshold?: number;
  }>(),
  {
    locale: 'zh-CN',
    ariaLabel: '',
    threshold: 100
  }
);

const SCROLL_THRESHOLD = computed(() => Math.max(0, props.threshold));
const RING_RADIUS = 17;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const mounted = ref(false);
const visible = ref(false);
const progress = ref(0);

const resolvedAriaLabel = computed(
  () => props.ariaLabel || SCROLL_TO_TOP_LABELS[props.locale] || SCROLL_TO_TOP_LABELS['zh-CN']
);

const ringOffset = computed(() => RING_CIRCUMFERENCE * (1 - progress.value));

function getPageScrollTop(): number {
  if (typeof window === 'undefined') return 0;
  return Math.max(
    window.scrollY,
    document.documentElement.scrollTop,
    document.body.scrollTop
  );
}

function scrollPageToTop(behavior: ScrollBehavior = 'auto') {
  if (typeof window === 'undefined') return;
  window.scrollTo({ top: 0, left: 0, behavior });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

const onScroll = () => {
  const scrollTop = getPageScrollTop();
  const docHeight = document.documentElement.scrollHeight - window.innerHeight;
  visible.value = scrollTop > SCROLL_THRESHOLD.value;
  progress.value = docHeight > 0 ? Math.min(1, Math.max(0, scrollTop / docHeight)) : 0;
};

const scrollToTop = () => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  scrollPageToTop(prefersReducedMotion ? 'auto' : 'smooth');
};

onMounted(() => {
  mounted.value = true;
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  onScroll();
});

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll);
  window.removeEventListener('resize', onScroll);
});
</script>
